import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { searchProjectIndex } from "../../../api/projectSearch";
import { projectArchivePath } from "./folders/archive-utils";

const SEARCH_DEBOUNCE_MS = 180;

function isMacPlatform() {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPod|iPhone|iPad/.test(navigator.platform);
}

export function projectPaletteShortcutLabel() {
  return isMacPlatform() ? "⌘K" : "Ctrl+K";
}

function moduleLabel(t, module) {
  const keys = {
    document: "project.palette.module.document",
    folder: "project.palette.module.folder",
    deviation: "project.palette.module.deviation",
    checklist: "project.palette.module.checklist",
    report: "project.palette.module.report",
    work_order: "project.palette.module.work_order",
    action: "project.palette.module.action",
  };
  return t(keys[module] || module);
}

function resultHref(item, projectId) {
  if (item.href) return item.href;
  if (item.module === "deviation") return `/dashboard/hse-ks/deviations/${item.id}`;
  if (item.module === "checklist") return `/dashboard/hse-ks/checklists/${item.id}`;
  if (item.module === "report") return `/dashboard/service/reports/${item.id}`;
  if (item.module === "work_order") return `/dashboard/work_orders/${item.id}`;
  if (item.module === "folder" || item.module === "document") {
    const folderId = item.folder_id || (item.module === "folder" ? item.id : "");
    return projectArchivePath(projectId, folderId);
  }
  return null;
}

/**
 * Project-scoped command palette (⌘K / Ctrl+K). Captures the shortcut so the
 * dashboard-wide GlobalSearch does not open on the project page.
 */
export function ProjectCommandPalette({
  open,
  onClose,
  onOpen,
  projectId,
  actions = [],
  onRunAction,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (open) onClose?.();
        else onOpen?.();
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [open, onClose, onOpen]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setDebouncedQuery("");
      setResults([]);
      return undefined;
    }
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, open]);

  useEffect(() => {
    if (!open || !projectId || debouncedQuery.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    searchProjectIndex({ projectId, query: debouncedQuery })
      .then((payload) => {
        if (cancelled) return;
        setResults(Array.isArray(payload?.data) ? payload.data : []);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setResults([]);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, open, projectId]);

  const visibleActions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return actions.filter((action) => {
      if (!action?.active) return false;
      if (!q) return true;
      return String(action.label || "").toLowerCase().includes(q);
    });
  }, [actions, query]);

  const groupedResults = useMemo(() => {
    const groups = {};
    for (const item of results) {
      if (!groups[item.module]) groups[item.module] = [];
      groups[item.module].push(item);
    }
    return Object.entries(groups);
  }, [results]);

  const handleAction = useCallback(
    (action) => {
      onClose?.();
      onRunAction?.(action);
    },
    [onClose, onRunAction]
  );

  const handleResult = useCallback(
    (item) => {
      const href = resultHref(item, projectId);
      onClose?.();
      if (href) navigate(href);
    },
    [navigate, onClose, projectId]
  );

  return (
    <Transition.Root show={open} as={Fragment} afterLeave={() => setQuery("")}>
      <Dialog
        as="div"
        className="relative z-[70]"
        onClose={onClose}
        initialFocus={inputRef}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-[rgba(18,28,42,.34)]" />
        </Transition.Child>

        <div className="fixed inset-0 z-[70] overflow-y-auto p-4 pt-[14vh] sm:p-6">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="mx-auto w-full max-w-[540px] overflow-hidden rounded-[14px] bg-white shadow-[0_24px_60px_rgba(15,25,40,.28)]">
              <div className="flex items-center gap-2.5 border-b border-[#eef2f7] px-4 py-3.5">
                <MagnifyingGlassIcon className="h-5 w-5 text-[#8b98a8]" aria-hidden />
                <input
                  ref={inputRef}
                  className="h-7 w-full border-0 bg-transparent p-0 text-[14.5px] text-slate-800 placeholder:text-[#8b98a8] focus:outline-none focus:ring-0"
                  placeholder={t("project.palette.placeholder")}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  aria-label={t("project.palette.placeholder")}
                />
              </div>

              <div className="max-h-[min(28rem,60vh)] overflow-y-auto px-2 py-2">
                {visibleActions.length > 0 ? (
                  <div className="mb-2">
                    <div className="px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#8b98a8]">
                      {t("project.palette.actions_heading")}
                    </div>
                    {visibleActions.map((action) => (
                      <button
                        key={action.label}
                        type="button"
                        onClick={() => handleAction(action)}
                        className="flex w-full cursor-pointer items-center gap-2.5 rounded-[9px] px-2.5 py-2.5 text-left text-sm font-medium text-[#33445a] hover:bg-[#f2f5f9]"
                      >
                        {action.icon ? (
                          <action.icon className="h-4 w-4 shrink-0 text-[#5b6b7f]" aria-hidden />
                        ) : null}
                        {action.label}
                      </button>
                    ))}
                  </div>
                ) : null}

                {loading ? (
                  <div className="px-2.5 py-3 text-sm text-[#7a8798]">
                    {t("project.palette.searching")}
                  </div>
                ) : null}

                {!loading && groupedResults.length > 0
                  ? groupedResults.map(([module, items]) => (
                      <div key={module} className="mb-2">
                        <div className="px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#8b98a8]">
                          {moduleLabel(t, module)}
                        </div>
                        {items.map((item) => (
                          <button
                            key={`${item.module}-${item.id}`}
                            type="button"
                            onClick={() => handleResult(item)}
                            className="flex w-full cursor-pointer items-center gap-2.5 rounded-[9px] px-2.5 py-2.5 text-left text-sm font-medium text-[#33445a] hover:bg-[#f2f5f9]"
                          >
                            {item.displayTitle}
                          </button>
                        ))}
                      </div>
                    ))
                  : null}

                {!loading &&
                query.trim().length >= 2 &&
                groupedResults.length === 0 &&
                visibleActions.length === 0 ? (
                  <div className="px-2.5 py-3 text-sm text-[#7a8798]">
                    {t("project.palette.no_results", { query })}
                  </div>
                ) : null}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default ProjectCommandPalette;
